import React, { useEffect, useState } from "react";
import { useTable, useFilters, useGlobalFilter } from "react-table";

const DefaultColumnFilter = ({
  column: { filterValue, setFilter },
}) => {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder={`Search...`}
      style={{
        width: "100%",
        padding: "8px",
        borderRadius: "4px",
        border: "1px solid #ddd",
      }}
    />
  );
};

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch("https://api.solarstation.in/reviews/getAllReviews");
        if (!response.ok) {
          throw new Error("Failed to fetch reviews");
        }
        const data = await response.json();
        setReviews(data);
      } catch (err) {
        setError("Error fetching reviews");
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const handleAction = async (id, status) => {
    try {
      const response = await fetch(`https://api.solarstation.in/reviews/updateReviewById/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ approved: status }),
      });
  
      if (!response.ok) {
        throw new Error("Failed to update review");
      }
  
      // Update local state
      setReviews((prevReviews) =>
        prevReviews.map((review) =>
          review._id === id ? { ...review, approved: status } : review
        )
      );
    } catch (err) {
      console.error("Error updating review:", err);
    }
  };
  

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Rating",
        accessor: "rating",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Comment",
        accessor: "comment",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) => new Date(value).toLocaleDateString(),
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Approved",
        accessor: "approved",
        Cell: ({ value }) => {
          let label = "";
          let backgroundColor = "";
      
          if (value === "approved") {
            label = "Approved";
            backgroundColor = "green";
          } else if (value === "rejected") {
            label = "Rejected";
            backgroundColor = "red";
          } else {
            label = "Pending";
            backgroundColor = "orange";
          }
      
          return (
            <span
              style={{
                backgroundColor,
                color: "white",
                padding: "9px 10px",
                borderRadius: "20px",
                fontSize: "12px",
              }}
            >
              {label}
            </span>
          );
        },
        Filter: ({ column: { filterValue, setFilter } }) => (
          <select
            value={filterValue || ""}
            onChange={(e) => setFilter(e.target.value || undefined)}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          >
            <option value="">All</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="pending">Pending</option>
          </select>
        ),
      },
      
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const { approved, _id } = row.original;
      
          return (
            <div>
              {approved === "pending" && (
                <>
                  <button
                    onClick={() => handleAction(_id, "approved")}
                    style={{
                      backgroundColor: "green",
                      color: "white",
                      padding: "4px 8px",
                      borderRadius: "5px",
                      border: "none",
                      fontSize: "9px",
                     
                    }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleAction(_id, "rejected")}
                    style={{
                      backgroundColor: "red",
                      color: "white",
                      padding: "4px 8px",
                      borderRadius: "5px",
                      border: "none",
                      fontSize: "9px",
                     
                    }}
                  >
                    Reject
                  </button>
                </>
              )}
              {approved === "approved" && (
                <button
                  onClick={() => handleAction(_id, "rejected")}
                  style={{
                    backgroundColor:"red",
                    color: "white",
                    padding: "10px 10px",
                    borderRadius: "20px",
                    fontSize: "13px",
                  }}
                >
                  Reject
                </button>
              )}
              {approved === "rejected" && (
                <button
                  onClick={() => handleAction(_id, "approved")}
                  style={{
                    backgroundColor: "green",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Approve
                </button>
              )}
            </div>
          );
        },
      }
      
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
    state: { globalFilter },
  } = useTable(
    {
      columns,
      data: reviews,
    },
    useFilters,
    useGlobalFilter
  );

  if (loading) {
    return <div>Loading reviews...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="p-4 w-full mx-auto">
    
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Reviews</h1>

      <table {...getTableProps()} style={{ width: "100%", border: "1px solid #ddd", borderRadius: "8px" }}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()} style={{ padding: "8px", borderBottom: "2px solid #ddd" }}>
                  {column.render("Header")}
                  <div>{column.canFilter ? column.render("Filter") : null}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Reviews;
